import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Archive,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileCheck2,
  List,
  PartyPopper,
  RotateCw,
  Rows3,
  Send,
  Trash2
} from 'lucide-react';
import { Page } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, Tabs, TabsList, TabsTrigger } from '@/components/ui/menu';
import { SkeletonRow, TableScroll, TableShell, Td, Th, Tr } from '@/components/ui/table';
import { InvoiceDocument } from '@/features/invoices/InvoiceDocument';
import { InvoiceEditor } from '@/features/invoices/InvoiceEditor';
import {
  useConvertTentative,
  useRegenerateTentative,
  useTentativeInvoices
} from '@/hooks/useInvoices';
import { useBulkDeleteEntries } from '@/hooks/useEntries';
import { useCompanyInfo } from '@/hooks/useSettings';
import { useBillingNouns } from '@/hooks/useSession';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { hoursShort, money } from '@/lib/format';
import type { InvoiceDetail, InvoiceStatus } from '@/types';

/**
 * Quick Invoice walks a queue of auto-generated tentative invoices — one per
 * client with unbilled time — and lets you resolve each one.
 *
 * Every resolve action is optimistic: the invoice leaves the queue and the next
 * one appears immediately, with the network call in flight behind it. v1 also
 * did this for "Send" only; here it applies to every disposition.
 */
export function QuickInvoicePage() {
  const nouns = useBillingNouns();
  const bundle = useTentativeInvoices();
  const company = useCompanyInfo();
  const regenerate = useRegenerateTentative();
  const convert = useConvertTentative();
  const bulkDelete = useBulkDeleteEntries();

  const [view, setView] = useLocalStorage<'step' | 'list'>('billable.quickInvoiceView', 'step');
  const [index, setIndex] = useState(0);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<InvoiceDetail | null>(null);
  const [resolvedCount, setResolvedCount] = useState(0);

  const invoices = bundle.data?.invoices ?? [];
  const current = invoices[Math.min(index, Math.max(0, invoices.length - 1))];

  // Keep the cursor in range as invoices leave the queue.
  useEffect(() => {
    if (index > 0 && index >= invoices.length) setIndex(Math.max(0, invoices.length - 1));
  }, [index, invoices.length]);

  const resolve = (invoice: InvoiceDetail, status: InvoiceStatus) => {
    setResolvedCount((c) => c + 1);
    convert.mutate({ tentativeInvoice: invoice, status });
  };

  const actions = (
    <>
      <Tabs value={view} onValueChange={(v) => setView(v as 'step' | 'list')}>
        <TabsList>
          <TabsTrigger value="step">
            <Rows3 />
            One at a time
          </TabsTrigger>
          <TabsTrigger value="list">
            <List />
            List
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <Button
        variant="secondary"
        loading={regenerate.isPending}
        onClick={() => {
          setIndex(0);
          setResolvedCount(0);
          regenerate.mutate();
        }}
      >
        <RotateCw />
        Regenerate
      </Button>
    </>
  );

  if (bundle.isPending) {
    return (
      <Page wide title="Quick Invoice" description="Turn unbilled time into invoices, fast.">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="skeleton h-[42rem] rounded-2xl" />
          <div className="space-y-3">
            <div className="skeleton h-24 rounded-2xl" />
            <div className="skeleton h-40 rounded-2xl" />
          </div>
        </div>
      </Page>
    );
  }

  if (!invoices.length) {
    return (
      <Page wide title="Quick Invoice" actions={actions}>
        <Card>
          {resolvedCount > 0 ? (
            <EmptyState
              icon={PartyPopper}
              title="That's the lot"
              description={`You worked through ${resolvedCount} ${resolvedCount === 1 ? 'invoice' : 'invoices'}. Nothing left to bill.`}
              action={
                <Button variant="primary" asChild>
                  <Link to="/invoices">Go to invoices</Link>
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={FileCheck2}
              title="No unbilled time"
              description={`Every logged hour is already on an invoice. Log more time, or regenerate to double-check.`}
              action={
                <Button
                  variant="secondary"
                  loading={regenerate.isPending}
                  onClick={() => regenerate.mutate()}
                >
                  <RotateCw />
                  Regenerate
                </Button>
              }
            />
          )}
        </Card>
      </Page>
    );
  }

  return (
    <Page
      wide
      title="Quick Invoice"
      description={`${invoices.length} ${invoices.length === 1 ? 'invoice' : 'invoices'} ready to review.`}
      actions={actions}
    >
      {view === 'list' ? (
        <TableShell>
          <TableScroll minWidth="min-w-0">
            <thead>
              <tr>
                <Th width="16rem">{nouns.client}</Th>
                <Th width="14rem">{nouns.payer}</Th>
                <Th align="right" width="6rem">
                  Items
                </Th>
                <Th align="right" width="6rem">
                  Hours
                </Th>
                <Th align="right" width="8rem">
                  Amount
                </Th>
                <Th align="right" width="10rem">
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, i) => (
                <Tr key={invoice.id}>
                  <Td className="font-medium">{invoice.clientName ?? '—'}</Td>
                  <Td className={invoice.billTo ? 'truncate' : 'text-subtle'}>
                    {invoice.billTo?.name ?? 'Not set'}
                  </Td>
                  <Td align="right" className="tnum text-muted">
                    {invoice.entries.length}
                  </Td>
                  <Td align="right" className="tnum">
                    {hoursShort(invoice.totalHours)}
                  </Td>
                  <Td align="right" className="tnum font-medium">
                    {money(invoice.totalAmount)}
                  </Td>
                  <Td align="right">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setIndex(i);
                        setView('step');
                      }}
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableScroll>
        </TableShell>
      ) : current ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="min-w-0">
            <InvoiceDocument
              invoice={current}
              company={company.data}
              clientLabel={nouns.client}
            />
          </div>

          <aside className="space-y-4">
            <Card>
              <CardHeader
                title={current.clientName ?? '—'}
                description={`${current.entries.length} items · ${hoursShort(current.totalHours)} hours`}
              />
              <CardBody className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Button
                    variant="ghost"
                    size="iconSm"
                    aria-label="Previous"
                    disabled={index === 0}
                    onClick={() => setIndex((i) => i - 1)}
                  >
                    <ChevronLeft />
                  </Button>
                  <span className="tnum text-sm text-muted">
                    {index + 1} of {invoices.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="iconSm"
                    aria-label="Next"
                    disabled={index >= invoices.length - 1}
                    onClick={() => setIndex((i) => i + 1)}
                  >
                    <ChevronRight />
                  </Button>
                </div>

                <div
                  className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]"
                  role="progressbar"
                  aria-valuenow={index + 1}
                  aria-valuemin={1}
                  aria-valuemax={invoices.length}
                >
                  <div
                    className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-200"
                    style={{ width: `${((index + 1) / invoices.length) * 100}%` }}
                  />
                </div>

                <p className="tnum text-2xl font-semibold tracking-tight">
                  {money(current.totalAmount)}
                </p>

                {!current.billTo?.emails.length ? (
                  <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                    No billing email on file — set one before sending.
                  </p>
                ) : null}
              </CardBody>
            </Card>

            <div className="space-y-2">
              <Button variant="secondary" className="w-full" onClick={() => setEditing(true)}>
                Edit invoice
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="primary"
                  className="flex-1"
                  disabled={!current.billTo?.emails.length}
                  onClick={() => resolve(current, 'sent')}
                >
                  <Send />
                  Send
                </Button>
                <Menu>
                  <MenuTrigger asChild>
                    <Button variant="secondary" size="icon" aria-label="Other options">
                      <ChevronRight className="rotate-90" />
                    </Button>
                  </MenuTrigger>
                  <MenuContent>
                    <MenuItem onSelect={() => resolve(current, 'draft')}>
                      <FileCheck2 />
                      Save as draft
                    </MenuItem>
                    <MenuItem onSelect={() => resolve(current, 'sent')}>
                      <Send />
                      Save as sent
                    </MenuItem>
                    <MenuItem onSelect={() => resolve(current, 'paid')}>
                      <CheckCircle2 />
                      Save as paid
                    </MenuItem>
                    <MenuItem onSelect={() => resolve(current, 'archived')}>
                      <Archive />
                      Archive
                    </MenuItem>
                    <MenuSeparator />
                    <MenuItem destructive onSelect={() => setConfirmDelete(current)}>
                      <Trash2 />
                      Delete this time
                    </MenuItem>
                  </MenuContent>
                </Menu>
              </div>

              <Button
                variant="ghost"
                className="w-full"
                disabled={index >= invoices.length - 1}
                onClick={() => setIndex((i) => i + 1)}
              >
                Skip for now
              </Button>
            </div>
          </aside>
        </div>
      ) : null}

      {current ? (
        <InvoiceEditor open={editing} onOpenChange={setEditing} invoice={current} />
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Delete this time?"
        description={`All ${confirmDelete?.entries.length ?? 0} time entries on this invoice will be deleted from the timesheet. This cannot be undone.`}
        confirmLabel="Delete entries"
        destructive
        onConfirm={() => {
          if (!confirmDelete) return;
          bulkDelete.mutate({ ids: confirmDelete.entries.map((e) => e.id) });
          setResolvedCount((c) => c + 1);
          setConfirmDelete(null);
          void bundle.refetch();
        }}
      />
    </Page>
  );
}

export { SkeletonRow };
